import React, { useState, useEffect, useMemo } from 'react';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import Icon from '@material-ui/core/Icon';
import DialogContent from '@material-ui/core/DialogContent';
import Typography from '@material-ui/core/Typography';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { IMeterReadingsView } from '../../../../../../@types/model/transformer/reading/transformerReading';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import FirebaseStorageImage from '../../../../../customComponents/image/FirebaseStorageImage';
import CarouselImageViewer from '../../../../../customComponents/image/CarouselViewer';
import TransformerMeterReadingProblem from '../../../../../customComponents/TransformerMeterReadingProblem';
import Divider from '@material-ui/core/Divider';
import StadiumButton from '../../../../../customComponents/button/StadiumButton';
import DialogActions from '@material-ui/core/DialogActions';
import DataFunctions from '../../../../../../store/data/functions';
import { IRootState } from '../../../../../../@types/redux';
import { connect } from 'react-redux';
import { ITransformerAttribute } from '../../../../../../@types/model/transformer/transformerAttribute';
import { KeyboardDatePicker } from '@material-ui/pickers';
import moment from 'moment';
import { DATE_FORMAT_DEFAULT_NO_TIME } from '../../../../../../appConstants';
import UsageToggleButton from '../../../../../customComponents/button/UsageToggleButton';
import FileDropZone from '../../../../../customComponents/FileDropZone';
import FileUploadButton from '../../../../../customComponents/button/FileUploadButton';
import MultiFileUploadProgress from '../../../../../customComponents/file/MultiFileUploadProgress';
import TransformerFunctions from '../../../../../../store/transformer/functions';
import ProjectDialog from '../../../../../customComponentV2/dialog/ProjectDialog';

interface ITransformerMunicipalityReadingInfoDialogProps {
    reading : IMeterReadingsView;
    enabled ?: boolean;

    onSave : (reading : IMeterReadingsView) => void;

    attributes : Array<ITransformerAttribute>;
}

const TransformerMunicipalityReadingInfoDialogClass = (props : ITransformerMunicipalityReadingInfoDialogProps) : React.ReactElement => {
    const [open, setOpen] = useState<boolean>(false);
    const [imageSrcs, setImageSrcs] = useState<Array<string>>([]);
    const [isCarouselOpen, setIsCarouselOpen] = useState<boolean>(false);
    const [meterReading, setMeterReading] = useState<string>('');
    const [usage, setUsage] = useState<number>(0);
    const [conditions, setConditions] = useState<Array<string>>([]);
    const [other, setOther] = useState<string>('');
    const [date, setDate] = useState<moment.Moment>(moment.utc().startOf('day'));
    const [rollover, setRollover] = useState<boolean>(false);
    const [image, setImage] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState<boolean>(false);

    const setOtherValue = () : void => {
        const otherList = conditions.filter(x => props.attributes.findIndex(e => e.Value === x) === - 1); 

        if (otherList.length) {
            setOther(otherList[0]);
            setConditions(conditions.filter(x => x !== otherList[0]));
        }
    };

    useEffect(() => {
        setOtherValue();
    }, [props.attributes]);

    useEffect(() => {
        setOtherValue();
    }, [conditions]);

    const onClick = () : void => {
        setIsCarouselOpen(false);
        setOpen(true);
        setMeterReading(props.reading?.Reading.toString() ?? '');
        setUsage(props.reading?.Usage ?? 0);
        setConditions([ ...(props.reading?.Conditions ?? []) ]);
        setDate(props.reading ? moment.utc(props.reading.Date).startOf('day') : moment.utc().startOf('day'));
        setRollover(!!props.reading?.Rollover);
        setImage(null);
        setIsUploading(false);

        if (props.enabled) DataFunctions.getTransformerAttributes();
    };

    const onClose = () : void => {
        setIsCarouselOpen(false);
        setOpen(false);
    };

    const onImageUrlLoaded = (url : string) : void => {
        setImageSrcs([url]);
    };

    const onThumbClick = () : void => {
        setIsCarouselOpen(true);
    };

    const onCarouselClose = () : void => {
        setIsCarouselOpen(false);
    };

    const onDateChange = (newDate : moment.Moment | null) : void => {
        if (!newDate) return;
        setDate(newDate.startOf('day').asUTC());
    };

    const onOtherChange = (event : React.ChangeEvent<HTMLInputElement>) : void => {
        setOther(event.target.value);
    };

    const onMeterReadingChange = (event : React.ChangeEvent<HTMLInputElement>) : void => {
        if (!props.enabled) return;
        if (event.currentTarget.value.length > (props.reading?.NumberOfDigits ?? 0)) return;

        const lastReading = (props.reading?.Reading ?? 0) - (props.reading?.Usage ?? 0);
        setMeterReading(event.currentTarget.value);
        setUsage(Number(event.currentTarget.value) - lastReading);
    };

    const onConditionClick = (value : string) : void => {
        if (!props.enabled) return;

        const newConditions = [ ...conditions ];
        const index = newConditions.indexOf(value);

        if (index < 0) {
            newConditions.push(value);
        } else {
            newConditions.splice(index, 1);
        }

        setConditions(newConditions);
    };

    const onRolloverToggle = () : void => {
        setRollover(!rollover);
    };

    const onSaveClick = async () : Promise<void> => {
        if (!props.reading) return;
        if (!props.onSave) return;
        if (!props.enabled) return;
        if (!meterReading) return;

        const newConditions = conditions.slice();

        if (other) {
            newConditions.push(other);
        }

        props.onSave({
            ...props.reading,
            Reading: Number(meterReading),
            Usage: usage,
            Conditions: [ ...newConditions ],
            Date: date.startOf('day').valueOf(),
            Rollover: rollover,
            ImageFileName: image?.name ?? props.reading.ImageFileName ?? null,
        });
    
        setIsUploading(true);
    };

    const onFileChange = (file : File) : void => {
        if (!props.reading) return;
        const { Guid } = props.reading;
        setImage(new File([file.slice()], `${Guid}-${file.name}`, {
            type: file.type,
        }));
    };

    const onFileUpload = (file : File, guid : string) : any => {
        const { reading } = props;
        if (!reading) return null;
        
        return TransformerFunctions.uploadMunicipalityReadingFile(reading.EPNumber, file, guid, reading.ItemNumber);
    };

    const onDone = () : void => {
        onClose();
    };

    return (
        <div>
            <Tooltip title='Info'>
                <div>
                    <IconButton color='primary' onClick={onClick}
                        aria-label='Info'>
                        {
                            props.enabled ? (
                                <Icon>edit</Icon>
                            ) : (
                                <InfoOutlinedIcon />
                            )
                        }
                    </IconButton>
                </div>
            </Tooltip>
            <ProjectDialog
                title={`VIEW READING | ${props.reading?.EPNumber} | ${props.reading?.MeterNumber}`}
                isOpen={open}
                onClose={onClose}
                maxWidth={'md'}
                fullWidth
                isLoadingCircular={isUploading}
            >
                <DialogContent className='fdc flx1 ais hfill bcg0'>
                    {
                        !!props.reading &&
                        !isUploading &&
                        open &&
                        <div className='fdc flx1 ais jcc'>
                            <div className='fdr ais'>
                                <div className='fdc flx1 aifs pr5'>
                                    <FormControl fullWidth required>
                                        <TextField
                                            autoComplete='off'
                                            id='meter'
                                            label='Meter'
                                            value={props.reading.MeterNumber}
                                            margin='normal'
                                            required
                                            disabled
                                            className={'TextField'}
                                        />
                                    </FormControl>
                                </div>
                                <div className='fdc flx1 aifs pl5'>
                                    <FormControl fullWidth required error={meterReading.length > props.reading.NumberOfDigits}>
                                        <TextField
                                            autoComplete='off'
                                            id='meterReading'
                                            type='number'
                                            label='Meter Reading'
                                            value={meterReading}
                                            disabled={!props.enabled}
                                            onChange={onMeterReadingChange}
                                            margin='normal'
                                            helperText={`${meterReading.length}/${props.reading.NumberOfDigits}`}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position='end'>
                                                        kWh
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </FormControl>
                                </div>
                            </div>
                            <div className='fdr ais'>
                                <div className='fdc flx1 aifs pr5'>
                                    <FormControl fullWidth required>
                                        <TextField
                                            autoComplete='off'
                                            id='lastReading'
                                            label='Last Reading'
                                            value={props.reading.Reading - props.reading.Usage}
                                            disabled
                                            margin='normal'
                                            required
                                            className={'TextField'}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position='end'>
                                                        kWh
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </FormControl>
                                </div>
                                <div className='fdc flx1 aifs pl5'>
                                    <FormControl fullWidth required>
                                        <TextField
                                            autoComplete='off'
                                            id='usage'
                                            label='Usage'
                                            value={usage}
                                            margin='normal'
                                            disabled
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position='end'>
                                                        kWh
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </FormControl>
                                </div>
                            </div>
                            <div className='fdr ais mt15 mb15'>
                                <div className='fdc flx1 aifs pr5'>
                                    <FormControl fullWidth required>
                                        <KeyboardDatePicker
                                            value={date}
                                            label='Reading Date'
                                            type='municipality'
                                            format={DATE_FORMAT_DEFAULT_NO_TIME}
                                            fullWidth
                                            onChange={onDateChange}
                                            required
                                        />
                                    </FormControl>
                                </div>
                                <div className='fdc flx1 aic jcc pl5'>
                                    <UsageToggleButton text='Rollover?' value='Rollover?' selected={rollover}
                                        onClick={onRolloverToggle}
                                    />
                                </div>
                            </div>
                            <div className='fdr ais'>
                                <div className='fdc flx1 ais pr5'>
                                    <div className='fdr mb10'>
                                        <Typography className='fs16 lh37 cg3 flx1'>
                                            Uploaded Image
                                        </Typography>
                                        {
                                            props.enabled &&
                                            <FileUploadButton title={'Upload Image'} accept='image/*' onFileSelected={onFileChange} />
                                        }
                                    </div>
                                    {
                                        !image &&
                                        (props.reading?.ImageFileName || !props.enabled) &&
                                        <div className='h138 bw1 bocg3 aic jcc mximg'>
                                            {
                                                props.reading?.ImageFileName &&
                                                <FirebaseStorageImage
                                                    excludeThumbnail
                                                    onClick={onThumbClick}
                                                    onLoaded={onImageUrlLoaded}
                                                    fileName={props.reading.ImageFileName}
                                                    path={`electricalpoint/${props.reading.EPNumber}/municipality_readings`}
                                                    url={props.reading.ImageFileUrl}
                                                />
                                            }
                                        </div>
                                    }
                                    {
                                        props.enabled &&
                                        !props.reading?.ImageFileName &&
                                        <FileDropZone onFileSelected={onFileChange} />
                                    }
                                    {
                                        image &&
                                        <div className='h138 bw1 bocg3 aic jcc mximg'>
                                            <img className={'mxhi'} src={URL.createObjectURL(image)} onClick={onClick} />
                                        </div>
                                    }
                                    
                                </div>
                                <div className='fdc flx1 ais pl5'>
                                </div>
                            </div>
                            <div className='fdr ais mt15'>
                                <div className='fdc flx1 ais pr5'>
                                    <div className='fdc mb10'>
                                        <Typography className='fs16 lh37 cg3'>
                                            Problems
                                        </Typography>
                                    </div>
                                    <div className='fdr fww cw mb10'>
                                        {
                                            !props.enabled &&
                                            conditions.map((n, i) => (
                                                <TransformerMeterReadingProblem key={`${i}_${props.reading?.EPNumber}_problem`} text={n} />
                                            ))
                                        }
                                        {
                                            props.enabled &&
                                            props.attributes
                                                .filter(x => x.IsCondition)
                                                .map((n, i) => (
                                                    <TransformerMeterReadingProblem
                                                        enabled
                                                        checked={conditions.includes(n.Value)}
                                                        key={`${i}_${props.reading?.EPNumber}_problem`}
                                                        onClick={onConditionClick}
                                                        text={n.Value}
                                                    />
                                                ))
                                        }
                                    </div>
                                    {
                                        props.enabled && 
                                        <div className={'fdr pb10'}>
                                            <FormControl className={'flx1'}>
                                                <TextField
                                                    autoComplete='off'
                                                    id='other'
                                                    label='Other'
                                                    value={other}
                                                    margin='dense'
                                                    className={'TextField'}
                                                    onChange={onOtherChange}
                                                />
                                            </FormControl>
                                            <div className={'flx1'}/>
                                        </div>
                                    }
                                    <Divider />
                                </div>
                            </div>
                            {
                                imageSrcs.length > 0 &&
                                <CarouselImageViewer index={0} isOpen={isCarouselOpen} imageSrcs={imageSrcs} onClose={onCarouselClose} />
                            }
                        </div>
                    }
                    {
                        isUploading &&
                        <MultiFileUploadProgress files={image ? [image] : []} onFileUpload={onFileUpload} onDone={onDone} />
                    }
                </DialogContent>
                {
                    props.enabled &&
                    <DialogActions className='bcg0'>
                        <StadiumButton variant='contained' onClick={onSaveClick}>
                            SAVE
                        </StadiumButton>
                    </DialogActions>
                }
            </ProjectDialog>
        </div>
    );
};

const mapStateToProps = (state : IRootState) => {
    return {
        attributes: state.data.transformerAttributes,
    };
};

const TransformerMunicipalityReadingInfoDialog = connect(
    mapStateToProps,
)(TransformerMunicipalityReadingInfoDialogClass);

export default TransformerMunicipalityReadingInfoDialog;
